import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../theme';
import { getApiUrl } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { X, Mail, Lock, User, Loader } from 'lucide-react';
import { isAdminUser } from '../utils/roles';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type AuthMode = 'login' | 'register';

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const { loginWithGoogle, setToken, setUser } = useAuth();

  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
      const body = mode === 'login'
        ? { email, password }
        : { email, password, name };

      const response = await fetch(getApiUrl(endpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error en autenticacion');
      }

      // Guardar token y usuario
      localStorage.setItem('authToken', data.token);
      setToken(data.token);
      setUser(data.user);

      onSuccess?.();
      onClose();

      // Redirigir segun rol
      if (isAdminUser(data.user)) {
        navigate('/admin');
      } else if (data.user.role === 'VENUE_OWNER') {
        navigate('/owner/reservations');
      } else {
        navigate('/home');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    loginWithGoogle();
  };

  const resetModal = () => {
    setMode('login');
    setEmail('');
    setPassword('');
    setName('');
    setError(null);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const toggleMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setError(null);
  };

  // Styles - Futbolero Dark Theme
  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: '#1E1E1E',
    borderRadius: '16px',
    padding: '32px',
    width: '100%',
    maxWidth: '400px',
    position: 'relative',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
    border: `1px solid ${theme.colors.border}`,
  };

  const closeButtonStyle: React.CSSProperties = {
    position: 'absolute',
    top: '16px',
    right: '16px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.colors.textSecondary,
    transition: 'color 0.2s',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '8px',
  };

  const logoStyle: React.CSSProperties = {
    fontSize: '32px',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '28px',
    fontWeight: 700,
    color: theme.colors.primary,
    fontFamily: theme.fonts.heading,
    letterSpacing: '1px',
    textTransform: 'uppercase',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '14px',
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginBottom: '24px',
  };

  const inputContainerStyle: React.CSSProperties = {
    position: 'relative',
    marginBottom: '16px',
  };

  const inputIconStyle: React.CSSProperties = {
    position: 'absolute',
    left: '16px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: theme.colors.textSecondary,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px 16px 14px 48px',
    fontSize: '16px',
    backgroundColor: theme.colors.background,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '12px',
    outline: 'none',
    color: theme.colors.text,
    boxSizing: 'border-box',
    fontFamily: theme.fonts.body,
    transition: 'border-color 0.2s',
  };

  const primaryButtonStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px',
    fontSize: '16px',
    fontWeight: 700,
    backgroundColor: loading ? theme.colors.border : theme.colors.primary,
    color: '#000',
    border: 'none',
    borderRadius: '12px',
    cursor: loading ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontFamily: theme.fonts.body,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  };

  const dividerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    margin: '24px 0',
    gap: '16px',
  };

  const dividerLineStyle: React.CSSProperties = {
    flex: 1,
    height: '1px',
    backgroundColor: theme.colors.border,
  };

  const googleButtonStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px',
    fontSize: '16px',
    fontWeight: 500,
    backgroundColor: '#FFFFFF',
    color: '#1a1a1a',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    transition: 'all 0.2s',
    fontFamily: theme.fonts.body,
  };

  const toggleStyle: React.CSSProperties = {
    textAlign: 'center',
    marginTop: '20px',
    fontSize: '14px',
    color: theme.colors.textSecondary,
  };

  const toggleButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    color: theme.colors.primary,
    cursor: 'pointer',
    textDecoration: 'underline',
    fontFamily: theme.fonts.body,
    fontSize: '14px',
  };

  const footerStyle: React.CSSProperties = {
    fontSize: '11px',
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginTop: '20px',
    lineHeight: 1.5,
  };

  const errorStyle: React.CSSProperties = {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    color: '#ef4444',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px',
    textAlign: 'center',
    border: '1px solid rgba(239, 68, 68, 0.3)',
  };

  return (
    <div style={overlayStyle} onClick={handleClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <button
          style={closeButtonStyle}
          onClick={handleClose}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = theme.colors.text;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = theme.colors.textSecondary;
          }}
        >
          <X size={24} />
        </button>

        {/* Header */}
        <div style={headerStyle}>
          <span style={logoStyle}>&#9917;</span>
          <span style={titleStyle}>Futbolero</span>
        </div>

        <p style={subtitleStyle}>
          {mode === 'login' ? 'Ingresa a tu cuenta' : 'Crea tu cuenta'}
        </p>

        {error && <div style={errorStyle}>{error}</div>}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {mode === 'register' && (
            <div style={inputContainerStyle}>
              <User size={20} style={inputIconStyle} />
              <input
                type="text"
                placeholder="Nombre"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={inputStyle}
                required
                disabled={loading}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = theme.colors.primary;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = theme.colors.border;
                }}
              />
            </div>
          )}

          <div style={inputContainerStyle}>
            <Mail size={20} style={inputIconStyle} />
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
              required
              disabled={loading}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = theme.colors.primary;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = theme.colors.border;
              }}
            />
          </div>

          <div style={inputContainerStyle}>
            <Lock size={20} style={inputIconStyle} />
            <input
              type="password"
              placeholder="Contrasena"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={inputStyle}
              required
              disabled={loading}
              minLength={6}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = theme.colors.primary;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = theme.colors.border;
              }}
            />
          </div>

          <button
            type="submit"
            style={primaryButtonStyle}
            disabled={loading}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = '#00D96F';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = theme.colors.primary;
                e.currentTarget.style.transform = 'translateY(0)';
              }
            }}
          >
            {loading ? (
              <>
                <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                Cargando...
              </>
            ) : mode === 'login' ? (
              'Iniciar Sesion'
            ) : (
              'Registrarse'
            )}
          </button>
        </form>

        {/* Divider */}
        <div style={dividerStyle}>
          <div style={dividerLineStyle} />
          <span style={{ color: theme.colors.textSecondary, fontSize: '14px' }}>o</span>
          <div style={dividerLineStyle} />
        </div>

        {/* Google Login */}
        <button
          onClick={handleGoogleLogin}
          style={googleButtonStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f0f0f0';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#FFFFFF';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continuar con Google
        </button>

        {/* Toggle Login/Register */}
        <div style={toggleStyle}>
          {mode === 'login' ? (
            <span>
              No tenes cuenta?{' '}
              <button style={toggleButtonStyle} onClick={toggleMode}>
                Registrate
              </button>
            </span>
          ) : (
            <span>
              Ya tenes cuenta?{' '}
              <button style={toggleButtonStyle} onClick={toggleMode}>
                Inicia sesion
              </button>
            </span>
          )}
        </div>

        {/* Footer */}
        <p style={footerStyle}>
          Al continuar, aceptas nuestros{' '}
          <a href="/terms" style={{ color: theme.colors.primary }}>Terminos</a>
          {' '}y la{' '}
          <a href="/privacy" style={{ color: theme.colors.primary }}>Politica de privacidad</a>.
        </p>
      </div>

      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AuthModal;
